<template>
  <div
    v-if="lastPage !== 1"
    class="mt-[55px] flex justify-center md:mt-[123px]"
  >
    <slot
      v-if="hasPreviousPage"
      v-bind="{ canNavigateLeft, previousPage }"
      name="previous-button"
    >
      <PaginationButton
        :disabled="!canNavigateLeft"
        :page="previousPage"
        :aria-label="$t('navigation.pagination.previous')"
      >
        <IconPaginationArrowLeft />
      </PaginationButton>
    </slot>

    <slot
      name="first-page-button"
      v-bind="{ canNavigateLeft, previousPage, showFirst }"
    >
      <PaginationButton
        v-if="showFirst"
        :page="firstPage"
        :aria-label="$t('navigation.pagination.page', { page: firstPage })"
      >
        {{ firstPage }}
      </PaginationButton>
    </slot>

    <slot v-if="showFirstDots" name="page-dots">
      <div class="px-0 py-3 md:px-3">...</div>
    </slot>

    <slot name="page-buttons" v-bind="{ limitedPageNumbers, currentPage }">
      <PaginationButton
        v-for="page in limitedPageNumbers"
        :key="page"
        :is-active="currentPage === page"
        :page="page"
        :aria-label="$t('navigation.pagination.page', { page })"
      >
        {{ page }}
      </PaginationButton>
    </slot>

    <slot v-if="showLastDots" name="page-dots">
      <div class="px-0 py-3 md:px-3">...</div>
    </slot>

    <slot name="last-page-button" v-bind="{ lastPage, showLast }">
      <PaginationButton
        v-if="showLast"
        :page="lastPage"
        :aria-label="$t('navigation.pagination.page', { page: lastPage })"
      >
        {{ lastPage }}
      </PaginationButton>
    </slot>

    <slot
      v-if="hasNextPage"
      name="next-button"
      v-bind="{ canNavigateRight, nextPage }"
    >
      <PaginationButton
        :disabled="!canNavigateRight"
        :page="nextPage"
        :aria-label="$t('navigation.pagination.next')"
      >
        <IconPaginationArrowRight />
      </PaginationButton>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import usePagination from '#layers/base/composables/usePagination'
import PaginationButton from '~/components/ui/pagination/PaginationButton.vue'

type PaginationProps = {
  firstPage: number
  currentPage: number
  lastPage: number
  visible?: number
}

const props = withDefaults(defineProps<PaginationProps>(), {
  visible: 5,
})

const { visible, firstPage, currentPage, lastPage } = toRefs(props)

const {
  hasPreviousPage,
  canNavigateLeft,
  previousPage,
  showFirst,
  showFirstDots,
  limitedPageNumbers,
  showLastDots,
  showLast,
  hasNextPage,
  canNavigateRight,
  nextPage,
} = usePagination({
  firstPage,
  currentPage,
  lastPage,
  visiblePages: visible,
})
</script>
